import { createContext, useContext, useState } from "react";

export const translations = {
  en: {
    nav: {
      about: "About",
      practices: "Practices",
      specialties: "Specialties",
      expat: "Expat Life",
      sessions: "Sessions",
      contact: "Contact",
      book: "Book a session",
    },
    hero: {
      eyebrow: "Sophrology · Reiki · Mindfulness",
      line1: "Return to",
      line2: "your inner",
      line3: "calm.",
      subtitle:
        "Cécile DC Kinnunen — French sophrologist and Reiki practitioner. Private sessions and small groups, online and in person in Katy & Houston, Texas.",
      book: "Book a session",
      discover: "Discover the practice",
      badge: "Katy · Houston · Online · EN / FR ·",
    },
    marquee: {
      words: ["Sophrology", "Reiki", "Mindfulness", "Holistic Wellness", "Relaxation", "Personal Growth"],
    },
    about: {
      eyebrow: "About your practitioner",
      h2a: "Bonjour, I am ",
      h2b: "Cécile.",
      p1: "A French sophrologist and Reiki practitioner now based in Texas, I guide children, teenagers, parents-to-be and adults back to balance — one breath at a time.",
      p2: "My approach blends the structured relaxation of sophrology, the gentle energy of Reiki and the grounding of mindfulness. Every session is a pause: a protected moment to release tension, restore confidence and reconnect with yourself.",
      p3: "A multi-expat myself — I have lived in Asia, Europe, the Middle East and now the United States — I understand from the inside what it means to rebuild a life far from home.",
      chips: [
        "Private sessions",
        "Small groups",
        "Online & in person",
        "Sessions in English & French",
        "Multi-expat — Asia · Europe · Middle East · USA",
      ],
    },
    chapters: {
      eyebrow: "The practice",
      h2a: "Three paths, ",
      h2b: "one destination — you.",
      items: [
        {
          title: "Sophrology",
          tag: "Dynamic relaxation",
          text: "A structured blend of guided breathing, gentle movement and positive visualisation that restores harmony between body and mind — a practical toolkit you keep for life.",
        },
        {
          title: "Reiki",
          tag: "Energy healing",
          text: "A gentle Japanese practice where light, resting hands encourage deep relaxation, release accumulated tension and support your body's natural ability to rebalance.",
        },
        {
          title: "Mindfulness",
          tag: "Present-moment awareness",
          text: "Simple meditation and grounding techniques that quiet mental chatter, sharpen focus and help you meet each moment — at work, at school, at home — with clarity.",
        },
      ],
    },
    specialties: {
      eyebrow: "Specialties",
      h2a: "What we can ",
      h2b: "gently work on",
      items: [
        { title: "Stress & anxiety", text: "Release tension and quiet a busy mind." },
        { title: "Self-confidence", text: "Reconnect with your inner resources." },
        { title: "Focus & performance", text: "Sharpen mental clarity when it matters most." },
        { title: "Pre & post natal", text: "Gentle support through pregnancy and beyond." },
        { title: "Pain management", text: "Soften chronic pain through deep relaxation." },
        { title: "Sleep improvement", text: "Rebuild deep, restorative rest, night after night." },
        { title: "Concentration", text: "Train attention for study, work and sport." },
      ],
      audienceTitle: "For every stage of life",
      audienceText: "Children · Teenagers · Pregnant women · Adults",
    },
    expat: {
      eyebrow: "Expat life",
      h2a: "Far from home, ",
      h2b: "close to yourself.",
      introPre: "Expatriation is a beautiful adventure — and a real upheaval. As a multi-expat who has lived in ",
      introStrong: "Asia, Europe, the Middle East and now the United States",
      introPost: ", I know its joys and its storms from the inside.",
      challenges: [
        "Culture shock and the fatigue of constant adaptation",
        "Loneliness and the distance from family and lifelong friends",
        "Losing your bearings — identity in transition between two worlds",
        "Expat and third-culture children navigating their own journey",
        "The invisible mental load of rebuilding a home, again",
      ],
      quote: "Sophrology and mindfulness offer a safe space to land, breathe and rebuild your balance — in French or in English.",
      cta: "Talk it over with me",
      badge: "Asia · Europe · Middle East · USA",
    },
    formats: {
      eyebrow: "Sessions",
      h2a: "Choose the rhythm ",
      h2b: "that suits you",
      items: [
        {
          title: "Private sessions",
          text: "One-to-one guidance, fully tailored to your story and your goals — in person or by video, in English or French.",
        },
        {
          title: "Small groups",
          text: "The warmth of shared practice in intimate groups — a gentle way to discover sophrology and mindfulness together.",
        },
        {
          title: "Online & in person",
          text: "Based in Katy & Houston, Texas — sessions in person around the area, or online from wherever you are.",
        },
      ],
    },
    contact: {
      eyebrow: "Contact",
      h2a: "Begin your ",
      h2b: "journey inward.",
      intro: "A question, a first session, a workshop for your group or your company — write to me, call me, and let's find the right moment together.",
      name: "Name",
      namePh: "Your name",
      email: "Email",
      emailPh: "you@example.com",
      phone: "Phone",
      phoneOptional: "(optional)",
      phonePh: "Your phone number",
      message: "Message",
      messagePh: "Tell me a little about what you are looking for…",
      send: "Send message",
      sending: "Sending…",
      toastOk: "Thank you — your message has been sent. Cécile will get back to you shortly.",
      toastErr: "Something went wrong — please try again or reach out by phone or email.",
    },
    footer: {
      tagline: "Sophrology · Reiki · Mindfulness",
      disclaimer:
        "Sophrology and holistic wellness sessions are designed for relaxation and personal growth. They are not medical or psychological treatments.",
    },
  },
  fr: {
    nav: {
      about: "À propos",
      practices: "Pratiques",
      specialties: "Spécialités",
      expat: "Vie d'expat",
      sessions: "Séances",
      contact: "Contact",
      book: "Réserver une séance",
    },
    hero: {
      eyebrow: "Sophrologie · Reiki · Pleine conscience",
      line1: "Retrouvez",
      line2: "votre calme",
      line3: "intérieur.",
      subtitle:
        "Cécile DC Kinnunen — sophrologue française et praticienne Reiki. Séances privées et petits groupes, en ligne et en présentiel à Katy & Houston, Texas.",
      book: "Réserver une séance",
      discover: "Découvrir la pratique",
      badge: "Katy · Houston · En ligne · EN / FR ·",
    },
    marquee: {
      words: ["Sophrologie", "Reiki", "Pleine conscience", "Bien-être holistique", "Relaxation", "Développement personnel"],
    },
    about: {
      eyebrow: "Votre praticienne",
      h2a: "Bonjour, je suis ",
      h2b: "Cécile.",
      p1: "Sophrologue française et praticienne Reiki installée au Texas, j'accompagne enfants, adolescents, futurs parents et adultes vers l'équilibre — une respiration à la fois.",
      p2: "Mon approche associe la relaxation structurée de la sophrologie, la douceur énergétique du Reiki et l'ancrage de la pleine conscience. Chaque séance est une parenthèse : un moment protégé pour relâcher les tensions, restaurer la confiance et renouer avec vous-même.",
      p3: "Multi-expatriée moi-même — j'ai vécu en Asie, en Europe, au Moyen-Orient et maintenant aux États-Unis — je comprends de l'intérieur ce que signifie reconstruire une vie loin de chez soi.",
      chips: [
        "Séances privées",
        "Petits groupes",
        "En ligne & en présentiel",
        "Séances en français & anglais",
        "Multi-expatriée — Asie · Europe · Moyen-Orient · USA",
      ],
    },
    chapters: {
      eyebrow: "La pratique",
      h2a: "Trois chemins, ",
      h2b: "une destination — vous.",
      items: [
        {
          title: "Sophrologie",
          tag: "Relaxation dynamique",
          text: "Un savant mélange de respiration guidée, de mouvements doux et de visualisation positive qui restaure l'harmonie entre le corps et l'esprit — une boîte à outils pratique pour toute la vie.",
        },
        {
          title: "Reiki",
          tag: "Soin énergétique",
          text: "Une pratique japonaise douce où l'apposition légère des mains favorise une relaxation profonde, libère les tensions accumulées et soutient la capacité naturelle du corps à se rééquilibrer.",
        },
        {
          title: "Pleine conscience",
          tag: "Présence à l'instant",
          text: "Des techniques simples de méditation et d'ancrage qui apaisent le bavardage mental, aiguisent la concentration et vous aident à vivre chaque moment — au travail, à l'école, à la maison — avec clarté.",
        },
      ],
    },
    specialties: {
      eyebrow: "Spécialités",
      h2a: "Ce que l'on peut ",
      h2b: "apaiser ensemble",
      items: [
        { title: "Stress & anxiété", text: "Relâchez les tensions et apaisez un mental agité." },
        { title: "Confiance en soi", text: "Renouez avec vos ressources intérieures." },
        { title: "Concentration & performance", text: "Aiguisez votre clarté mentale quand cela compte le plus." },
        { title: "Pré & post natal", text: "Un accompagnement en douceur pendant la grossesse et après." },
        { title: "Gestion de la douleur", text: "Adoucissez les douleurs chroniques grâce à la relaxation profonde." },
        { title: "Sommeil retrouvé", text: "Retrouvez un sommeil profond et réparateur, nuit après nuit." },
        { title: "Concentration", text: "Entraînez votre attention pour les études, le travail et le sport." },
      ],
      audienceTitle: "Pour chaque étape de la vie",
      audienceText: "Enfants · Adolescents · Femmes enceintes · Adultes",
    },
    expat: {
      eyebrow: "Vie d'expat",
      h2a: "Loin de chez soi, ",
      h2b: "proche de soi-même.",
      introPre: "L'expatriation est une belle aventure — et un véritable bouleversement. Multi-expatriée, j'ai vécu en ",
      introStrong: "Asie, en Europe, au Moyen-Orient et maintenant aux États-Unis",
      introPost: ", et j'en connais les joies et les tempêtes de l'intérieur.",
      challenges: [
        "Le choc culturel et la fatigue de l'adaptation permanente",
        "La solitude et la distance avec la famille et les amis de toujours",
        "La perte de repères — une identité en transition entre deux mondes",
        "Les enfants expatriés et les enfants de la troisième culture, qui vivent leur propre parcours",
        "La charge mentale invisible de reconstruire un foyer, encore une fois",
      ],
      quote: "La sophrologie et la pleine conscience offrent un espace sûr pour atterrir, respirer et reconstruire votre équilibre — en français ou en anglais.",
      cta: "Parlons-en ensemble",
      badge: "Asie · Europe · Moyen-Orient · USA",
    },
    formats: {
      eyebrow: "Séances",
      h2a: "Choisissez le rythme ",
      h2b: "qui vous convient",
      items: [
        {
          title: "Séances privées",
          text: "Un accompagnement individuel, entièrement adapté à votre histoire et à vos objectifs — en présentiel ou en visio, en français ou en anglais.",
        },
        {
          title: "Petits groupes",
          text: "La chaleur de la pratique partagée en groupes intimistes — une façon douce de découvrir la sophrologie et la pleine conscience ensemble.",
        },
        {
          title: "En ligne & en présentiel",
          text: "Basée à Katy & Houston, Texas — séances en présentiel dans la région, ou en ligne où que vous soyez.",
        },
      ],
    },
    contact: {
      eyebrow: "Contact",
      h2a: "Commencez votre ",
      h2b: "voyage intérieur.",
      intro: "Une question, une première séance, un atelier pour votre groupe ou votre entreprise — écrivez-moi, appelez-moi, et trouvons ensemble le bon moment.",
      name: "Nom",
      namePh: "Votre nom",
      email: "Email",
      emailPh: "vous@exemple.com",
      phone: "Téléphone",
      phoneOptional: "(facultatif)",
      phonePh: "Votre numéro de téléphone",
      message: "Message",
      messagePh: "Parlez-moi un peu de ce que vous recherchez…",
      send: "Envoyer le message",
      sending: "Envoi…",
      toastOk: "Merci — votre message a bien été envoyé. Cécile vous répondra très vite.",
      toastErr: "Une erreur est survenue — réessayez ou contactez-moi par téléphone ou par email.",
    },
    footer: {
      tagline: "Sophrologie · Reiki · Pleine conscience",
      disclaimer:
        "Les séances de sophrologie et de bien-être holistique sont conçues pour la relaxation et le développement personnel. Elles ne constituent ni un traitement médical ni un traitement psychologique.",
    },
  },
};

const LangContext = createContext(null);

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("hb-lang") || "en");
  const toggle = () => {
    const next = lang === "en" ? "fr" : "en";
    setLang(next);
    localStorage.setItem("hb-lang", next);
  };
  return <LangContext.Provider value={{ lang, toggle, t: translations[lang] }}>{children}</LangContext.Provider>;
}

export function useLang() {
  return useContext(LangContext);
}
